import { getIsPageNavigationItem } from '@app/logic/lookbook';
import { flattenLookbookNavigationItems } from '@app/logic/navigation';
import { useLookbookPages } from '@app/services/hooks/lookbook/useLookbookPages';
import { isDefined, isNull } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { listToRecord } from '@app/utils/normalize';
import { Lookbook } from '@typings';
import React from 'react';

interface Context {
  addedMenuItemsRef: React.RefObject<HTMLDivElement>;
  isLoadingPages: boolean;
  isUnavailablePage: boolean;
  addedMenuItems: Lookbook.NavigationItem[];
  searchPhrase: string;
  setNavigationItems: React.Dispatch<React.SetStateAction<Lookbook.NavigationItem[]>>;
  setSearchPhrase: React.Dispatch<React.SetStateAction<string>>;
  itemRecords: Record<string, Lookbook.NavigationItem>;
  pagesRecords: Record<string, Lookbook.Page>;
  scrollAddedMenuItems: () => void;
}

export const LookbookNavigationSettingsContext = React.createContext<Context | null>(null);

export const useLookbookNavigationSettingsContext = (): Context => {
  const context = React.useContext(LookbookNavigationSettingsContext);
  if (isNull(context)) {
    throw new Error('LookbookNavigationSettingsContext can not be used outside the scope of LookbookNavigationSettingsContextProvider');
  }

  return context;
};

interface Props {
  lookbookId?: Nullable<string>;
  addedMenuItems: Lookbook.NavigationItem[];
  setNavigationItems: React.Dispatch<React.SetStateAction<Lookbook.NavigationItem[]>>;
}

export const LookbookNavigationSettingsContextProvider = ({
  children,
  lookbookId,
  addedMenuItems,
  setNavigationItems,
}: React.WithChildren<Props>) => {
  const [searchPhrase, setSearchPhrase] = React.useState('');
  const addedMenuItemsRef = React.useRef<HTMLDivElement>(null);

  const { data: lookbookPages = [], isLoading: isLoadingPages } = useLookbookPages({ lookbookId });
  const pagesRecords = listToRecord(lookbookPages, 'id');
  const itemRecords = React.useMemo(() => {
    return listToRecord(flattenLookbookNavigationItems(addedMenuItems), 'id');
  }, [addedMenuItems]);

  const itemsWithOrderIndex = React.useMemo(
    () => addedMenuItems.flatMap<Lookbook.NavigationItem & { orderIndex: number }>((item, idx) => ({ ...item, orderIndex: idx })),
    [addedMenuItems],
  );

  const isUnavailablePage = React.useMemo(() => {
    if (isEmpty(pagesRecords)) {
      return false;
    }

    return itemsWithOrderIndex.some(page => getIsPageNavigationItem(page) && !isDefined(pagesRecords[page.pageId]));
  }, [itemsWithOrderIndex, pagesRecords]);

  const scrollAddedMenuItems = React.useCallback(() => {
    requestAnimationFrame(() => {
      addedMenuItemsRef.current?.scrollTo({ behavior: 'smooth', top: addedMenuItemsRef.current.scrollHeight });
    });
  }, []);

  return (
    <LookbookNavigationSettingsContext.Provider
      value={{
        addedMenuItems,
        addedMenuItemsRef,
        isLoadingPages,
        isUnavailablePage,
        itemRecords,
        pagesRecords,
        scrollAddedMenuItems,
        searchPhrase,
        setNavigationItems,
        setSearchPhrase,
      }}
    >
      {children}
    </LookbookNavigationSettingsContext.Provider>
  );
};
